// Centralised Marketplace fetch. In dev, Vite's server.proxy maps
// `/api/v1/marketplace/*` to MARKETPLACE_BASE_URL, so a relative path works.
// In prod (static SPA), the same relative path would 404 — so we resolve
// against the configured base URL. The two modes converge on the same
// fetch URL pattern from the caller's perspective.

import type { MarketplaceAgentManifest } from './types';

export function marketplaceFetchUrl(baseUrl: string, path: string): string {
  const trimmed = baseUrl.replace(/\/+$/, '');
  return `${trimmed}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Tag for the failure reason — callers use it to pick error copy. */
export type MarketplaceFetchReason = 'network' | 'http' | 'parse';

/**
 * Typed error thrown by {@link fetchMarketplaceAgent}. Lets the
 * `fetchManifest` wrapper route the failure into its `onError` callback
 * without string-parsing — and lets other callers (`AgentDetailClient`,
 * connect/install routes) distinguish a 5xx from a network drop if they
 * want to surface different copy.
 */
export class MarketplaceFetchError extends Error {
  readonly reason: MarketplaceFetchReason;
  readonly detail: unknown;
  constructor(reason: MarketplaceFetchReason, detail: unknown, message: string) {
    super(message);
    this.name = 'MarketplaceFetchError';
    this.reason = reason;
    this.detail = detail;
  }
}

// In-memory cache + in-flight dedup. The dashboard hydrates cards for every
// connected agent on mount; opening AgentDetailClient immediately after would
// otherwise refetch the same manifest. TTL is short — the manifest can change
// when a publisher pushes a new version, but a 60s window covers the common
// "list → detail" navigation without serving stale data for long.
const CACHE_TTL_MS = 60_000;
const cache = new Map<string, { value: MarketplaceAgentManifest | null; expires: number }>();
const inflight = new Map<string, Promise<MarketplaceAgentManifest | null>>();

async function doFetch(
  baseUrl: string,
  agentId: string,
): Promise<MarketplaceAgentManifest | null> {
  const url = marketplaceFetchUrl(baseUrl, `/api/v1/marketplace/agents/${encodeURIComponent(agentId)}`);
  let res: Response;
  try {
    res = await fetch(url, { cache: 'no-store' });
  } catch (err) {
    throw new MarketplaceFetchError(
      'network',
      err,
      `marketplace agent fetch failed (network): ${err instanceof Error ? err.message : String(err)}`,
    );
  }
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new MarketplaceFetchError('http', { status: res.status }, `marketplace agent fetch failed: ${res.status}`);
  }
  try {
    return (await res.json()) as MarketplaceAgentManifest;
  } catch (err) {
    throw new MarketplaceFetchError(
      'parse',
      err,
      `marketplace agent fetch failed (parse): ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

export async function fetchMarketplaceAgent(
  baseUrl: string,
  agentId: string,
): Promise<MarketplaceAgentManifest | null> {
  const key = `${baseUrl}|${agentId}`;
  const cached = cache.get(key);
  if (cached && cached.expires > Date.now()) return cached.value;
  const existing = inflight.get(key);
  if (existing) return existing;

  const p = doFetch(baseUrl, agentId)
    .then((value) => {
      cache.set(key, { value, expires: Date.now() + CACHE_TTL_MS });
      return value;
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, p);
  return p;
}

/** Test-only: drop the manifest cache + any in-flight requests. */
export function _resetMarketplaceCacheForTests(): void {
  cache.clear();
  inflight.clear();
}
